import { supabase } from '../lib/supabase';
import { overtimeService } from './overtimeService';
import { getTodayInTimezone, getFirstDayOfMonthInTimezone, getFirstDayOfYearInTimezone } from '../utils/formatters';
import { getThumbnailPath } from '../utils/imageUtils';

/**
 * Get all companies a contractor is associated with
 */
export const getContractorCompanies = async (contractorId) => {
  try {
    const { data, error } = await supabase?.from('company_users')?.select(`
        id,
        company_id,
        company_alias,
        role,
        is_active,
        companies:company_id (
          id,
          company_name,
          company_tag,
          status
        )
      `)?.eq('user_id', contractorId)?.eq('is_active', true)?.eq('role', 'contractor');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching contractor companies:', error);
    throw error;
  }
};

/**
 * Get contractor dashboard metrics for a specific company
 * @param {string} contractorId - The contractor's user ID
 * @param {string} companyId - The company ID
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney')
 */
export const getContractorDashboardMetrics = async (contractorId, companyId, timezone = 'Australia/Sydney') => {
  try {
    const today = getTodayInTimezone(timezone);
    const now = new Date();

    // Calculate yesterday's date to include late-night jobs
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    // Get today's jobs AND yesterday's late-night jobs (only assigned and in_progress - not submitted/approved)
    const { data: rawTodayJobs, error: todayError } = await supabase?.from('job_instances')?.select(`
        id,
        instance_number,
        status,
        scheduled_date,
        scheduled_time,
        scheduled_end_time,
        scheduled_duration,
        jobs:job_id (
          id,
          job_name,
          location_address,
          payment_amount,
          payment_type,
          priority,
          estimated_hours,
          service_receiver_id,
          is_internal,
          client_id,
          job_site_id,
          original_address,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          client:clients(id, client_name),
          job_site:job_sites(id, site_name, address)
        )
      `)
      ?.eq('assigned_contractor', contractorId)
      ?.eq('company_id', companyId)
      ?.gte('scheduled_date', yesterdayStr)
      ?.lte('scheduled_date', today)
      ?.in('status', ['assigned', 'in_progress'])
      ?.order('scheduled_time', { ascending: true });

    if (todayError) {
      console.error('Error fetching today jobs:', todayError);
      throw todayError;
    }

    // Filter: include jobs that have NOT yet reached their completion time
    // Jobs past completion time go to "Pending Jobs" section instead
    const MINIMUM_VISIBILITY_HOURS = 12;

    const todayJobs = (rawTodayJobs || []).filter(instance => {
      const job = instance.jobs;
      const scheduledDate = instance.scheduled_date;

      // Calculate job completion time using helper function
      const completionTime = calculateJobCompletionTime(instance, job, scheduledDate);

      // EXCLUDE jobs past their completion time - they belong in Pending Jobs section
      if (now >= completionTime) {
        return false;
      }

      // Include today's jobs that haven't completed yet
      if (instance.scheduled_date === today) {
        return true;
      }

      // Yesterday's jobs: include if within 12-hour buffer AND not past completion (already checked above)
      const minimumDeadline = new Date(completionTime.getTime() + (MINIMUM_VISIBILITY_HOURS * 60 * 60 * 1000));
      return now < minimumDeadline;
    });

    // Get upcoming jobs count
    const { count: upcomingCount, error: upcomingError } = await supabase?.from('job_instances')?.select('id', { count: 'exact', head: true })?.eq('assigned_contractor', contractorId)?.eq('company_id', companyId)?.gt('scheduled_date', today);

    if (upcomingError) {
      console.error('Error fetching upcoming jobs:', upcomingError);
      throw upcomingError;
    }

    // Get date boundaries for filtering using company timezone
    const firstDayOfMonth = getFirstDayOfMonthInTimezone(timezone);
    const firstDayOfYear = getFirstDayOfYearInTimezone(timezone);

    // Get ALL timesheets for this contractor (we'll filter in JS for monthly/yearly/all-time)
    const { data: allTimesheets, error: timesheetError } = await supabase?.from('timesheets')?.select('amount_earned, status, created_at')?.eq('contractor_id', contractorId)?.eq('company_id', companyId);

    if (timesheetError) {
      console.error('Error fetching timesheets:', timesheetError);
      throw timesheetError;
    }

    const approvedTimesheets = allTimesheets?.filter(t => t?.status === 'approved') || [];

    // Monthly earnings
    const monthlyEarning = approvedTimesheets
      ?.filter(t => t?.created_at >= firstDayOfMonth)
      ?.reduce((sum, t) => sum + (parseFloat(t?.amount_earned) || 0), 0) || 0;

    // Yearly earnings
    const yearlyEarnings = approvedTimesheets
      ?.filter(t => t?.created_at >= firstDayOfYear)
      ?.reduce((sum, t) => sum + (parseFloat(t?.amount_earned) || 0), 0) || 0;

    // All-time earnings
    const allTimeEarnings = approvedTimesheets
      ?.reduce((sum, t) => sum + (parseFloat(t?.amount_earned) || 0), 0) || 0;

    // Get monthly completed jobs count
    const { count: monthlyCompletedCount, error: monthlyCompletedError } = await supabase?.from('job_instances')?.select('id', { count: 'exact', head: true })?.eq('assigned_contractor', contractorId)?.eq('company_id', companyId)?.eq('status', 'approved')?.gte('scheduled_date', firstDayOfMonth);

    if (monthlyCompletedError) {
      console.error('Error fetching monthly completed:', monthlyCompletedError);
      throw monthlyCompletedError;
    }

    // Get yearly completed jobs count
    const { count: yearlyCompletedCount, error: yearlyCompletedError } = await supabase?.from('job_instances')?.select('id', { count: 'exact', head: true })?.eq('assigned_contractor', contractorId)?.eq('company_id', companyId)?.eq('status', 'approved')?.gte('scheduled_date', firstDayOfYear);

    if (yearlyCompletedError) {
      console.error('Error fetching yearly completed:', yearlyCompletedError);
      throw yearlyCompletedError;
    }

    // Get all-time completed jobs count (only approved jobs count as completed)
    const { count: allTimeCompletedCount, error: allTimeCompletedError } = await supabase?.from('job_instances')?.select('id', { count: 'exact', head: true })?.eq('assigned_contractor', contractorId)?.eq('company_id', companyId)?.eq('status', 'approved');

    if (allTimeCompletedError) {
      console.error('Error fetching all-time completed:', allTimeCompletedError);
      throw allTimeCompletedError;
    }

    // Get new unassigned public jobs (only from published, active jobs)
    // Note: Supabase doesn't support filtering on nested relations directly,
    // so we fetch and filter in JS. Using regular join (not !inner) to avoid RLS conflicts.
    const { data: allUnassignedJobs, error: newJobsError } = await supabase?.from('job_instances')?.select(`
        id,
        instance_number,
        status,
        scheduled_date,
        scheduled_time,
        jobs:job_id (
          id,
          job_name,
          description,
          location_address,
          payment_amount,
          payment_type,
          priority,
          service_receiver_id,
          is_internal,
          is_published,
          status,
          client_id,
          job_site_id,
          original_address,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          client:clients(id, client_name),
          job_site:job_sites(id, site_name, address)
        )
      `)
      ?.eq('company_id', companyId)
      ?.eq('status', 'unassigned')
      ?.is('assigned_contractor', null)
      ?.order('scheduled_date', { ascending: true });

    if (newJobsError) {
      console.error('Error fetching unassigned jobs:', newJobsError);
      throw newJobsError;
    }

    // Filter to only include jobs that are published AND active (and have job data)
    const newJobs = (allUnassignedJobs || [])
      ?.filter(instance => instance?.jobs && instance?.jobs?.is_published === true && instance?.jobs?.status === 'active')
      ?.slice(0, 10);

    return {
      todayJobs: todayJobs || [],
      upcomingJobsCount: upcomingCount || 0,
      availableJobsCount: newJobs?.length || 0,
      monthlyEarnings: monthlyEarning?.toFixed(2),
      yearlyEarnings: yearlyEarnings?.toFixed(2),
      allTimeEarnings: allTimeEarnings?.toFixed(2),
      monthlyCompletedJobs: monthlyCompletedCount || 0,
      yearlyCompletedJobs: yearlyCompletedCount || 0,
      allTimeCompletedJobs: allTimeCompletedCount || 0,
      newPublicJobs: newJobs || []
    };
  } catch (error) {
    console.error('Error fetching contractor dashboard metrics:', error);
    throw error;
  }
};

/**
 * Get today's jobs for contractor
 * Includes jobs scheduled for today AND jobs from yesterday that are still within
 * the 12-hour visibility window (e.g., a job that started at 11 PM yesterday and
 * goes for 2 hours should still be visible until at least 1 PM today)
 *
 * @param {string} contractorId - The contractor's user ID
 * @param {string} companyId - The company ID
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney')
 */
export const getTodayJobs = async (contractorId, companyId, timezone = 'Australia/Sydney') => {
  try {
    const today = getTodayInTimezone(timezone);
    const now = new Date();

    // Calculate yesterday's date to include late-night jobs
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    console.log('=== getTodayJobs ===');
    console.log('contractorId:', contractorId);
    console.log('companyId:', companyId);
    console.log('today (in timezone ' + timezone + '):', today);
    console.log('yesterday:', yesterdayStr);

    // Fetch jobs from yesterday and today
    const { data, error } = await supabase?.from('job_instances')?.select(`
        id,
        instance_number,
        status,
        scheduled_date,
        scheduled_time,
        scheduled_end_time,
        scheduled_duration,
        clock_in_time,
        clock_out_time,
        checklist_data,
        questionnaire_responses,
        work_notes,
        jobs:job_id (
          id,
          job_name,
          description,
          location_address,
          location_lat,
          location_lng,
          payment_amount,
          payment_type,
          priority,
          estimated_hours,
          service_receiver_id,
          is_internal,
          client_id,
          job_site_id,
          original_address,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          client:clients(id, client_name),
          job_site:job_sites(id, site_name, address, address_lat, address_lng)
        )
      `)
      ?.eq('assigned_contractor', contractorId)
      ?.eq('company_id', companyId)
      ?.gte('scheduled_date', yesterdayStr)
      ?.lte('scheduled_date', today)
      ?.in('status', ['assigned', 'in_progress'])
      ?.order('scheduled_time', { ascending: true });

    if (error) throw error;

    // Filter: include today's jobs OR yesterday's jobs that are still within 12-hour window
    const MINIMUM_VISIBILITY_HOURS = 12;

    const filteredJobs = (data || []).filter(instance => {
      // Always include today's jobs
      if (instance.scheduled_date === today) {
        return true;
      }

      // For yesterday's jobs, check if still within 12-hour visibility window
      const job = instance.jobs;
      const scheduledDate = instance.scheduled_date;

      // Calculate job completion time using the helper logic
      let completionTime;

      if (instance.scheduled_end_time) {
        completionTime = new Date(`${scheduledDate}T${instance.scheduled_end_time}`);
      } else if (instance.scheduled_duration) {
        const startTime = instance.scheduled_time || '09:00:00';
        const startDateTime = new Date(`${scheduledDate}T${startTime}`);
        const durationHours = parseFloat(instance.scheduled_duration) || 2;
        completionTime = new Date(startDateTime.getTime() + (durationHours * 60 * 60 * 1000));
      } else if (instance.scheduled_time && job?.estimated_hours) {
        const startDateTime = new Date(`${scheduledDate}T${instance.scheduled_time}`);
        const durationHours = parseFloat(job.estimated_hours);
        completionTime = new Date(startDateTime.getTime() + (durationHours * 60 * 60 * 1000));
      } else if (instance.scheduled_time) {
        const startDateTime = new Date(`${scheduledDate}T${instance.scheduled_time}`);
        completionTime = new Date(startDateTime.getTime() + (2 * 60 * 60 * 1000));
      } else {
        completionTime = new Date(`${scheduledDate}T23:59:59`);
      }

      // Calculate minimum visibility deadline (12 hours after completion)
      const minimumDeadline = new Date(completionTime.getTime() + (MINIMUM_VISIBILITY_HOURS * 60 * 60 * 1000));

      // Include if still within the 12-hour window
      return now < minimumDeadline;
    });

    console.log('getTodayJobs result:', { data: filteredJobs, originalCount: data?.length, filteredCount: filteredJobs.length, error });
    if (filteredJobs?.length > 0) {
      console.log('Job statuses:', filteredJobs.map(d => ({ id: d.id, status: d.status, scheduled_date: d.scheduled_date })));
    }

    return filteredJobs;
  } catch (error) {
    console.error('Error fetching today jobs:', error);
    throw error;
  }
};

/**
 * Get upcoming jobs for contractor
 * @param {string} contractorId - The contractor's user ID
 * @param {string} companyId - The company ID
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney')
 */
export const getUpcomingJobs = async (contractorId, companyId, timezone = 'Australia/Sydney') => {
  try {
    const today = getTodayInTimezone(timezone);

    const { data, error } = await supabase?.from('job_instances')?.select(`
        id,
        instance_number,
        status,
        scheduled_date,
        scheduled_time,
        scheduled_end_time,
        scheduled_duration,
        jobs:job_id (
          id,
          job_name,
          description,
          location_address,
          payment_amount,
          payment_type,
          priority,
          service_receiver_id,
          is_internal,
          client_id,
          job_site_id,
          original_address,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          client:clients(id, client_name),
          job_site:job_sites(id, site_name, address)
        )
      `)?.eq('assigned_contractor', contractorId)?.eq('company_id', companyId)?.gt('scheduled_date', today)?.in('status', ['assigned', 'in_progress'])?.order('scheduled_date', { ascending: true })?.order('scheduled_time', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching upcoming jobs:', error);
    throw error;
  }
};

/**
 * Get earnings data for contractor
 */
export const getContractorEarnings = async (contractorId, companyId, filters = {}) => {
  try {
    let query = supabase?.from('timesheets')?.select(`
        id,
        amount_earned,
        status,
        total_hours,
        clock_in_time,
        clock_out_time,
        approved_at,
        created_at,
        contractor_confirmed_received,
        contractor_confirmed_at,
        contractor_confirmation_note,
        job_instance:job_instance_id (
          id,
          scheduled_date,
          instance_number,
          scheduled_time,
          status,
          work_notes,
          checklist_data,
          questionnaire_responses,
          photos:job_photos(id, storage_path, thumbnail_path, photo_type, uploaded_at),
          jobs:job_id (
            id,
            job_display_id,
            job_name,
            job_type,
            description,
            location_address,
            priority,
            status,
            payment_type,
            payment_amount,
            contractor_include_gst,
            contractor_gst_type,
            estimated_hours,
            recurring_schedule,
            photos_required_before,
            photos_required_after,
            questionnaire_template,
            safety_requirements,
            equipment_list,
            other_details,
            clock_in_radius,
            contact_person_name,
            contact_person_phone,
            contact_person_email,
            created_at,
            updated_at,
            service_receiver_id,
            is_internal,
            client_id,
            job_site_id,
            original_address,
            service_receiver:service_receivers(id, receiver_name, receiver_type, contact_person, contact_email, contact_phone, address),
            client:clients(id, client_name, contact_person, contact_email, contact_phone, address),
            job_site:job_sites(id, site_name, address, contact_person, contact_email, contact_phone)
          )
        )
      `)?.eq('contractor_id', contractorId)?.eq('company_id', companyId)?.order('created_at', { ascending: false });

    // Apply filters
    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Generate signed URLs for photos (both full and thumbnail)
    if (data) {
      for (const timesheet of data) {
        if (timesheet.job_instance?.photos?.length > 0) {
          timesheet.job_instance.photos = await Promise.all(
            timesheet.job_instance.photos.map(async (photo) => {
              if (photo.storage_path) {
                const thumbnailPath = photo.thumbnail_path || getThumbnailPath(photo.storage_path);

                // Fetch both URLs in parallel
                const [fullUrlResult, thumbUrlResult] = await Promise.all([
                  supabase?.storage?.from('job-photos')?.createSignedUrl(photo.storage_path, 3600),
                  supabase?.storage?.from('job-photos')?.createSignedUrl(thumbnailPath, 3600),
                ]);

                const url = fullUrlResult?.data?.signedUrl;
                const thumbnailUrl = thumbUrlResult?.data?.signedUrl || url;

                return { ...photo, url, thumbnailUrl };
              }
              return photo;
            })
          );
        }
      }
    }

    // Calculate totals - include paid timesheets in totalEarnings
    const totalEarnings = data
      ?.filter(t => t?.status === 'approved' || t?.status === 'paid')
      ?.reduce((sum, t) => sum + (parseFloat(t?.amount_earned) || 0), 0) || 0;

    const pendingEarnings = data
      ?.filter(t => t?.status === 'pending')
      ?.reduce((sum, t) => sum + (parseFloat(t?.amount_earned) || 0), 0) || 0;

    const totalJobs = data?.length || 0;

    return {
      timesheets: data || [],
      totalEarnings: totalEarnings?.toFixed(2),
      pendingEarnings: pendingEarnings?.toFixed(2),
      totalJobs
    };
  } catch (error) {
    console.error('Error fetching contractor earnings:', error);
    throw error;
  }
};

/**
 * Update job instance progress (for auto-save)
 */
export const updateJobInstanceProgress = async (instanceId, updates) => {
  try {
    const { data, error } = await supabase?.from('job_instances')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', instanceId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating job instance:', error);
    throw error;
  }
};

/**
 * Clock in for a job
 */
export const clockIn = async (instanceId, location) => {
  try {
    // Get current user to ensure they can only clock in to their own assigned jobs
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('job_instances')?.update({
        status: 'in_progress',
        clock_in_time: new Date()?.toISOString(),
        clock_in_location: location?.lat && location?.lng ? `(${location.lat},${location.lng})` : null,
        clock_out_time: null,
        clock_out_location: null
      })?.eq('id', instanceId)?.eq('assigned_contractor', user?.id)?.select()?.single();

    if (error) throw error;
    if (!data) throw new Error('Job not found or not assigned to you');
    return data;
  } catch (error) {
    console.error('Error clocking in:', error);
    throw error;
  }
};

/**
 * Clock out from a job with payment cap
 * This is a simpler version used by the Today Jobs page
 * Returns { jobInstance, timesheet, hasOvertime, overtimeInfo }
 */
export const clockOut = async (instanceId, location) => {
  try {
    // Fetch instance with job payment details including estimated_hours
    const { data: instance } = await supabase?.from('job_instances')?.select(`
      clock_in_time,
      assigned_contractor,
      company_id,
      job:jobs!inner(payment_type, payment_amount, estimated_hours)
    `)?.eq('id', instanceId)?.single();

    const clockOutTime = new Date();
    const clockInTime = new Date(instance?.clock_in_time);
    const actualHours = (clockOutTime - clockInTime) / (1000 * 60 * 60);

    const { data, error } = await supabase?.from('job_instances')?.update({
        status: 'submitted',
        clock_out_time: clockOutTime?.toISOString(),
        clock_out_location: location?.lat && location?.lng ? `(${location.lat},${location.lng})` : null
      })?.eq('id', instanceId)?.select()?.single();

    if (error) throw error;

    // Get payment configuration from job
    const paymentType = instance.job?.payment_type || 'hourly';
    const paymentAmount = parseFloat(instance.job?.payment_amount) || 0;
    const estimatedHours = instance.job?.estimated_hours ? parseFloat(instance.job.estimated_hours) : null;

    // Calculate amount earned with payment cap for hourly jobs
    let amountEarned = 0;
    let cappedHours = null;
    let hasOvertime = false;
    let overtimeInfo = null;

    if (paymentType === 'hourly') {
      // Use overtimeService to calculate with rounding rules and cap
      const calculation = overtimeService.calculateCappedPayment(actualHours, estimatedHours, paymentAmount);

      cappedHours = calculation.cappedHours;
      amountEarned = calculation.basePay;
      hasOvertime = calculation.hasOvertime;

      if (hasOvertime) {
        overtimeInfo = {
          estimatedHours: calculation.estimatedHours,
          actualHours: calculation.billableHours,
          overtimeHours: calculation.overtimeHours,
          hourlyRate: calculation.hourlyRate,
          basePay: calculation.basePay,
          potentialOvertimePay: calculation.overtimeAmount,
          totalPotential: calculation.totalPotential
        };
      }
    } else {
      // Fixed rate - full amount regardless of hours
      amountEarned = paymentAmount;
      cappedHours = actualHours;
    }

    // Create timesheet entry with capped amount
    const { data: timesheetData, error: timesheetError } = await supabase?.from('timesheets')?.insert({
        job_instance_id: instanceId,
        contractor_id: instance?.assigned_contractor,
        company_id: instance?.company_id,
        clock_in_time: instance?.clock_in_time,
        clock_out_time: clockOutTime?.toISOString(),
        total_hours: parseFloat(actualHours.toFixed(2)),
        estimated_hours: estimatedHours,
        capped_hours: parseFloat(cappedHours.toFixed(2)),
        base_amount: parseFloat(amountEarned.toFixed(2)),
        amount_earned: parseFloat(amountEarned.toFixed(2)),
        overtime_amount: 0,
        has_overtime_request: false,
        status: 'pending'
      })?.select()?.single();

    if (timesheetError) throw timesheetError;

    return { jobInstance: data, timesheet: timesheetData, hasOvertime, overtimeInfo };
  } catch (error) {
    console.error('Error clocking out:', error);
    throw error;
  }
};

/**
 * Confirm payment receipt for a timesheet
 * Called by contractor to acknowledge they received payment in their bank account
 * Industry standard: Two-sided acknowledgment system for payment reconciliation
 *
 * @param {string} timesheetId - The timesheet ID to confirm
 * @param {string} contractorId - The contractor's user ID (for RLS security)
 * @param {string|null} note - Optional note (e.g., bank reference number)
 * @returns {Promise<Object>} Updated timesheet data
 */
export const confirmPaymentReceived = async (timesheetId, contractorId, note = null) => {
  try {
    const { data, error } = await supabase
      ?.from('timesheets')
      ?.update({
        contractor_confirmed_received: true,
        contractor_confirmed_at: new Date().toISOString(),
        contractor_confirmation_note: note
      })
      ?.eq('id', timesheetId)
      ?.eq('contractor_id', contractorId) // RLS: only own timesheets
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error confirming payment receipt:', error);
    throw error;
  }
};

/**
 * Undo payment confirmation (in case of mistake)
 * Allows contractor to unmark a payment if they clicked by accident
 *
 * @param {string} timesheetId - The timesheet ID to unconfirm
 * @param {string} contractorId - The contractor's user ID (for RLS security)
 * @returns {Promise<Object>} Updated timesheet data
 */
export const unconfirmPaymentReceived = async (timesheetId, contractorId) => {
  try {
    const { data, error } = await supabase
      ?.from('timesheets')
      ?.update({
        contractor_confirmed_received: false,
        contractor_confirmed_at: null,
        contractor_confirmation_note: null
      })
      ?.eq('id', timesheetId)
      ?.eq('contractor_id', contractorId) // RLS: only own timesheets
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error unconfirming payment receipt:', error);
    throw error;
  }
};

/**
 * Calculate the expected job completion time based on job details
 * Uses: scheduled_end_time > (scheduled_time + estimated_hours) > (scheduled_time + 2 hours default)
 *
 * @param {Object} instance - Job instance with scheduled_time, scheduled_end_time, scheduled_duration
 * @param {Object} job - Job details with estimated_hours
 * @param {string} scheduledDate - The scheduled date string (YYYY-MM-DD)
 * @returns {Date} Expected completion time
 */
const calculateJobCompletionTime = (instance, job, scheduledDate) => {
  // Priority 1: Use instance's scheduled_end_time if available
  if (instance.scheduled_end_time) {
    return new Date(`${scheduledDate}T${instance.scheduled_end_time}`);
  }

  // Priority 2: Use instance's scheduled_duration if available
  if (instance.scheduled_duration) {
    const startTime = instance.scheduled_time || '09:00:00';
    const startDateTime = new Date(`${scheduledDate}T${startTime}`);
    const durationHours = parseFloat(instance.scheduled_duration) || 2;
    return new Date(startDateTime.getTime() + (durationHours * 60 * 60 * 1000));
  }

  // Priority 3: Calculate from scheduled_time + estimated_hours
  if (instance.scheduled_time && job?.estimated_hours) {
    const startDateTime = new Date(`${scheduledDate}T${instance.scheduled_time}`);
    const durationHours = parseFloat(job.estimated_hours);
    return new Date(startDateTime.getTime() + (durationHours * 60 * 60 * 1000));
  }

  // Priority 4: Default to scheduled_time + 2 hours, or end of day
  if (instance.scheduled_time) {
    const startDateTime = new Date(`${scheduledDate}T${instance.scheduled_time}`);
    return new Date(startDateTime.getTime() + (2 * 60 * 60 * 1000));
  }

  // Fallback: End of scheduled date
  return new Date(`${scheduledDate}T23:59:59`);
};

/**
 * Get pending jobs for contractor (past scheduled date but still within submission deadline)
 * These are jobs where clock_in_flexibility is enabled and the deadline hasn't passed
 *
 * Key logic for job visibility:
 * 1. Jobs with clock_in_flexibility enabled remain visible until their deadline
 * 2. Deadline = job completion time + late_submission_window_hours (default 24)
 * 3. Job completion time = scheduled_end_time OR (scheduled_time + estimated_hours) OR (scheduled_time + 2h default)
 * 4. MINIMUM visibility: 12 hours after job completion time (even without clock_in_flexibility)
 *
 * @param {string} contractorId - The contractor's user ID
 * @param {string} companyId - The company ID
 * @param {string} timezone - IANA timezone string (e.g., 'Australia/Sydney')
 * @returns {Promise<Array>} Array of pending job instances
 */
export const getPendingJobs = async (contractorId, companyId, timezone = 'Australia/Sydney') => {
  try {
    const today = getTodayInTimezone(timezone);
    const now = new Date();

    // Calculate the earliest date we need to check (today - 7 days to cover all possible late submission windows)
    const earliestDate = new Date(now);
    earliestDate.setDate(earliestDate.getDate() - 7);
    const earliestDateStr = earliestDate.toISOString().split('T')[0];

    // Fetch jobs from the last 7 days (or today) with assigned/in_progress status
    // We include today because a job might be past its completion time + 12 hours already today
    // Includes late submission columns added by migration
    const { data, error } = await supabase
      ?.from('job_instances')
      ?.select(`
        id,
        instance_number,
        status,
        scheduled_date,
        scheduled_time,
        scheduled_end_time,
        scheduled_duration,
        clock_in_time,
        clock_out_time,
        submission_deadline,
        allow_late_submission_override,
        jobs:job_id (
          id,
          job_display_id,
          job_name,
          description,
          location_address,
          payment_amount,
          payment_type,
          priority,
          estimated_hours,
          service_receiver_id,
          is_internal,
          clock_in_flexibility,
          late_submission_window_hours,
          client_id,
          job_site_id,
          original_address,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          client:clients(id, client_name),
          job_site:job_sites(id, site_name, address)
        )
      `)
      ?.eq('assigned_contractor', contractorId)
      ?.eq('company_id', companyId)
      ?.gte('scheduled_date', earliestDateStr)
      ?.lte('scheduled_date', today)
      ?.in('status', ['assigned', 'in_progress'])
      ?.order('scheduled_date', { ascending: false });

    if (error) throw error;

    // Filter in JS to check deadline logic and 12-hour buffer
    const MINIMUM_VISIBILITY_HOURS = 12;

    const pendingJobs = (data || []).filter(instance => {
      const job = instance.jobs;
      const scheduledDate = instance.scheduled_date;

      // Calculate job completion time
      const completionTime = calculateJobCompletionTime(instance, job, scheduledDate);

      // Check if we're past the job completion time (otherwise it's still a "today" job)
      if (now < completionTime) {
        return false; // Still an active job, not a pending one
      }

      // Calculate minimum deadline (completion time + 12 hours buffer)
      const minimumDeadline = new Date(completionTime.getTime() + (MINIMUM_VISIBILITY_HOURS * 60 * 60 * 1000));

      // Check if late submission is allowed for this instance
      // Note: allow_late_submission_override and late_submission_window_hours may not exist yet
      const allowLate = instance.allow_late_submission_override ?? job?.clock_in_flexibility;

      if (allowLate) {
        // Calculate the late submission deadline (default 24 hours if column doesn't exist)
        const windowHours = job?.late_submission_window_hours || 24;
        const lateDeadline = new Date(completionTime.getTime() + (windowHours * 60 * 60 * 1000));

        // Use manually set deadline if available, otherwise use calculated one
        const deadline = instance.submission_deadline ? new Date(instance.submission_deadline) : lateDeadline;

        // Include if within deadline
        return now < deadline;
      } else {
        // Even without clock_in_flexibility, ensure 12-hour minimum visibility
        return now < minimumDeadline;
      }
    });

    // Add computed deadline and days overdue to each job
    return pendingJobs.map(instance => {
      const job = instance.jobs;
      const scheduledDate = instance.scheduled_date;

      // Calculate job completion time
      const completionTime = calculateJobCompletionTime(instance, job, scheduledDate);

      // Calculate minimum deadline (12 hours after completion)
      const minimumDeadline = new Date(completionTime.getTime() + (MINIMUM_VISIBILITY_HOURS * 60 * 60 * 1000));

      // Check if late submission is allowed
      const allowLate = instance.allow_late_submission_override ?? job?.clock_in_flexibility;

      let deadline;
      if (allowLate) {
        const windowHours = job?.late_submission_window_hours || 24;
        const lateDeadline = new Date(completionTime.getTime() + (windowHours * 60 * 60 * 1000));
        deadline = instance.submission_deadline ? new Date(instance.submission_deadline) : lateDeadline;
      } else {
        deadline = minimumDeadline;
      }

      // Calculate days overdue (from scheduled date)
      const scheduledDateObj = new Date(scheduledDate);
      const todayDate = new Date(today);
      const daysOverdue = Math.floor((todayDate - scheduledDateObj) / (1000 * 60 * 60 * 24));

      // Time remaining until deadline
      const timeRemainingMs = deadline - now;
      const hoursRemaining = Math.max(0, Math.floor(timeRemainingMs / (1000 * 60 * 60)));

      // Extension requests not available until migration is run
      const pendingExtensionRequest = null;

      return {
        ...instance,
        computedDeadline: deadline,
        completionTime,
        daysOverdue,
        hoursRemaining,
        pendingExtensionRequest,
        hasLateSubmissionEnabled: allowLate
      };
    });
  } catch (error) {
    console.error('Error fetching pending jobs:', error);
    throw error;
  }
};

/**
 * Get action-required jobs for contractor (needs_revision or disputed)
 * These are reports that were rejected or disputed by corporate and need contractor response
 *
 * @param {string} contractorId - The contractor's user ID
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of action-required job instances with feedback
 */
export const getActionRequiredJobs = async (contractorId, companyId) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return [];

    // Try with notes column first
    let { data, error } = await supabase
      ?.from('job_instances')
      ?.select(`
        id,
        instance_number,
        status,
        scheduled_date,
        scheduled_time,
        notes,
        updated_at,
        clock_in_time,
        clock_out_time,
        jobs:job_id (
          id,
          job_display_id,
          job_name,
          description,
          location_address,
          payment_amount,
          payment_type,
          priority,
          estimated_hours,
          service_receiver_id,
          is_internal,
          client_id,
          job_site_id,
          original_address,
          service_receiver:service_receivers(id, receiver_name, receiver_type),
          client:clients(id, client_name),
          job_site:job_sites(id, site_name, address)
        )
      `)
      ?.eq('assigned_contractor', contractorId)
      ?.eq('company_id', companyId)
      ?.in('status', ['needs_revision', 'disputed'])
      ?.order('updated_at', { ascending: false });

    // If notes column doesn't exist, retry without it
    // Check various error message patterns Supabase might return
    if (error && (error.message?.includes('notes') || error.message?.includes('schema cache') || error.code === '42703')) {
      console.warn('notes column not found in job_instances, proceeding without it. Error:', error.message);
      const retryResult = await supabase
        ?.from('job_instances')
        ?.select(`
          id,
          instance_number,
          status,
          scheduled_date,
          scheduled_time,
          updated_at,
          clock_in_time,
          clock_out_time,
          jobs:job_id (
            id,
            job_display_id,
            job_name,
            description,
            location_address,
            payment_amount,
            payment_type,
            priority,
            estimated_hours,
            service_receiver_id,
            is_internal,
            client_id,
            job_site_id,
            original_address,
            service_receiver:service_receivers(id, receiver_name, receiver_type),
            client:clients(id, client_name),
            job_site:job_sites(id, site_name, address)
          )
        `)
        ?.eq('assigned_contractor', contractorId)
        ?.eq('company_id', companyId)
        ?.in('status', ['needs_revision', 'disputed'])
        ?.order('updated_at', { ascending: false });

      data = retryResult.data;
      error = retryResult.error;
    }

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching action-required jobs:', error);
    throw error;
  }
};

/**
 * Request a deadline extension for a job instance
 *
 * @param {string} instanceId - Job instance ID
 * @param {string} contractorId - Contractor user ID
 * @param {string} companyId - Company ID
 * @param {Date} requestedDeadline - Requested new deadline
 * @param {string} reason - Reason for extension
 * @returns {Promise<Object>} Created extension request
 */
export const requestExtension = async (instanceId, contractorId, companyId, requestedDeadline, reason) => {
  try {
    // Check if there's already a pending request for this instance
    const { data: existing, error: checkError } = await supabase
      ?.from('job_extension_requests')
      ?.select('id')
      ?.eq('job_instance_id', instanceId)
      ?.eq('status', 'pending')
      ?.single();

    // Handle case where table doesn't exist yet
    if (checkError && checkError.code === '42P01') {
      throw new Error('Extension requests feature is not yet available. Please run the database migration first.');
    }

    if (checkError && checkError.code !== 'PGRST116') throw checkError; // PGRST116 is "no rows"
    if (existing) {
      throw new Error('You already have a pending extension request for this job');
    }

    const { data, error } = await supabase
      ?.from('job_extension_requests')
      ?.insert({
        job_instance_id: instanceId,
        contractor_id: contractorId,
        company_id: companyId,
        requested_deadline: requestedDeadline.toISOString(),
        reason: reason,
        status: 'pending'
      })
      ?.select()
      ?.single();

    if (error) {
      // Handle case where table doesn't exist
      if (error.code === '42P01') {
        throw new Error('Extension requests feature is not yet available. Please run the database migration first.');
      }
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Error requesting extension:', error);
    throw error;
  }
};

/**
 * Get all extension requests for a contractor
 *
 * @param {string} contractorId - Contractor user ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Array of extension requests
 */
export const getContractorExtensionRequests = async (contractorId, companyId) => {
  try {
    const { data, error } = await supabase
      ?.from('job_extension_requests')
      ?.select(`
        *,
        job_instance:job_instance_id (
          id,
          scheduled_date,
          instance_number,
          jobs:job_id (id, job_name, job_display_id)
        )
      `)
      ?.eq('contractor_id', contractorId)
      ?.eq('company_id', companyId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching extension requests:', error);
    throw error;
  }
};